import { useState, useRef, useCallback } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Camera, Upload, X, Loader2, Sparkles, TrendingUp, AlertCircle, ChevronRight } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { cn } from "@/lib/utils";

interface ValuationResult {
  item_name: string;
  category: string;
  condition_grade: string;
  condition_notes: string;
  price_estimate: {
    low: number;
    mid: number;
    high: number;
    currency: string;
  };
  confidence: "low" | "medium" | "high";
  market_insights: string;
  authentication_notes: string;
  comparable_sales: string[];
}

interface SnapToPriceModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onValuationComplete?: (valuation: ValuationResult, imageUrl: string) => void;
}

export function SnapToPriceModal({ open, onOpenChange, onValuationComplete }: SnapToPriceModalProps) {
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const [imageBase64, setImageBase64] = useState<string | null>(null);
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [valuation, setValuation] = useState<ValuationResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const cameraInputRef = useRef<HTMLInputElement>(null);

  const resetState = useCallback(() => {
    setImagePreview(null);
    setImageBase64(null);
    setValuation(null);
    setError(null);
    setIsAnalyzing(false);
  }, []);

  const handleClose = () => {
    resetState();
    onOpenChange(false);
  };

  const processImage = (file: File) => {
    const reader = new FileReader();
    reader.onloadend = () => {
      const result = reader.result as string;
      setImagePreview(result);
      // Extract base64 without the data URL prefix
      const base64 = result.split(",")[1];
      setImageBase64(base64);
      setError(null);
    };
    reader.readAsDataURL(file);
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      if (file.size > 10 * 1024 * 1024) {
        setError("Image must be under 10MB");
        return;
      }
      processImage(file);
    }
  };

  const analyzeImage = async () => {
    if (!imageBase64) return;

    setIsAnalyzing(true);
    setError(null);

    try {
      const { data, error: fnError } = await supabase.functions.invoke("snap-to-price", {
        body: { imageBase64 },
      });

      if (fnError) throw fnError;

      if (data.error) {
        throw new Error(data.error);
      }

      setValuation(data.valuation);
      
      if (onValuationComplete && imagePreview) {
        onValuationComplete(data.valuation, imagePreview);
      }

      toast.success("Valuation complete!");
    } catch (err) {
      console.error("Valuation error:", err);
      const message = err instanceof Error ? err.message : "Failed to analyze image";
      setError(message);
      toast.error(message);
    } finally {
      setIsAnalyzing(false);
    }
  };

  const confidenceColors = {
    low: "text-amber-500 bg-amber-500/10",
    medium: "text-blue-500 bg-blue-500/10",
    high: "text-emerald-500 bg-emerald-500/10",
  };

  const formatPrice = (price: number) => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(price);
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Sparkles className="h-5 w-5 text-primary" />
            Snap to Price
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          <AnimatePresence mode="wait">
            {!imagePreview ? (
              <motion.div
                key="upload"
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
                className="space-y-4"
              >
                <p className="text-sm text-muted-foreground">
                  Take a photo or upload an image of your collectible for an instant AI-powered valuation.
                </p>

                <div className="grid grid-cols-2 gap-3">
                  <Button
                    variant="outline"
                    className="h-32 flex-col gap-3 border-dashed hover:border-primary hover:bg-primary/5"
                    onClick={() => cameraInputRef.current?.click()}
                  >
                    <Camera className="h-8 w-8 text-muted-foreground" />
                    <span className="text-sm">Take Photo</span>
                  </Button>

                  <Button
                    variant="outline"
                    className="h-32 flex-col gap-3 border-dashed hover:border-primary hover:bg-primary/5"
                    onClick={() => fileInputRef.current?.click()}
                  >
                    <Upload className="h-8 w-8 text-muted-foreground" />
                    <span className="text-sm">Upload Image</span>
                  </Button>
                </div>

                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={handleFileChange}
                />
                <input
                  ref={cameraInputRef}
                  type="file"
                  accept="image/*"
                  capture="environment"
                  className="hidden"
                  onChange={handleFileChange}
                />
              </motion.div>
            ) : !valuation ? (
              <motion.div
                key="preview"
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
                className="space-y-4"
              >
                <div className="relative rounded-lg overflow-hidden bg-muted">
                  <img
                    src={imagePreview}
                    alt="Preview"
                    className="w-full h-64 object-contain"
                  />
                  <Button
                    variant="ghost"
                    size="icon"
                    className="absolute top-2 right-2 bg-background/80 backdrop-blur-sm"
                    onClick={resetState}
                  >
                    <X className="h-4 w-4" />
                  </Button>
                </div>

                {error && (
                  <div className="flex items-center gap-2 p-3 rounded-lg bg-destructive/10 text-destructive text-sm">
                    <AlertCircle className="h-4 w-4 shrink-0" />
                    {error}
                  </div>
                )}

                <Button
                  className="w-full"
                  size="lg"
                  onClick={analyzeImage}
                  disabled={isAnalyzing}
                >
                  {isAnalyzing ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      Analyzing...
                    </>
                  ) : (
                    <>
                      <Sparkles className="h-4 w-4 mr-2" />
                      Get Valuation
                    </>
                  )}
                </Button>
              </motion.div>
            ) : (
              <motion.div
                key="result"
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
                className="space-y-4"
              >
                {/* Image thumbnail */}
                <div className="flex gap-4">
                  <img
                    src={imagePreview}
                    alt="Item"
                    className="w-20 h-20 object-cover rounded-lg"
                  />
                  <div className="flex-1 min-w-0">
                    <h3 className="font-semibold truncate">{valuation.item_name}</h3>
                    <p className="text-sm text-muted-foreground">{valuation.category}</p>
                    <div className={cn(
                      "inline-flex items-center gap-1 mt-1 px-2 py-0.5 rounded-full text-xs font-medium",
                      confidenceColors[valuation.confidence]
                    )}>
                      {valuation.confidence} confidence
                    </div>
                  </div>
                </div>

                {/* Price estimate */}
                <div className="p-4 rounded-lg bg-gradient-to-br from-primary/10 to-primary/5 border border-primary/20">
                  <div className="flex items-center gap-2 mb-3">
                    <TrendingUp className="h-4 w-4 text-primary" />
                    <span className="text-sm font-medium">Estimated Value</span>
                  </div>
                  <div className="text-center">
                    <div className="text-3xl font-bold text-primary">
                      {formatPrice(valuation.price_estimate.mid)}
                    </div>
                    <div className="text-sm text-muted-foreground mt-1">
                      Range: {formatPrice(valuation.price_estimate.low)} - {formatPrice(valuation.price_estimate.high)}
                    </div>
                  </div>
                </div>

                {/* Condition */}
                <div className="p-3 rounded-lg bg-muted/50">
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-muted-foreground">Condition</span>
                    <span className="font-medium">{valuation.condition_grade}</span>
                  </div>
                  {valuation.condition_notes && (
                    <p className="text-xs text-muted-foreground mt-1">{valuation.condition_notes}</p>
                  )}
                </div>

                {/* Market insights */}
                {valuation.market_insights && (
                  <div className="p-3 rounded-lg bg-muted/50">
                    <span className="text-sm font-medium">Market Insights</span>
                    <p className="text-sm text-muted-foreground mt-1">{valuation.market_insights}</p>
                  </div>
                )}

                {/* Authentication notes */}
                {valuation.authentication_notes && (
                  <div className="p-3 rounded-lg bg-amber-500/10 border border-amber-500/20">
                    <span className="text-sm font-medium text-amber-600 dark:text-amber-400">Authentication Notes</span>
                    <p className="text-sm text-muted-foreground mt-1">{valuation.authentication_notes}</p>
                  </div>
                )}

                {/* Comparable sales */}
                {valuation.comparable_sales && valuation.comparable_sales.length > 0 && (
                  <div className="space-y-2">
                    <span className="text-sm font-medium">Recent Comparables</span>
                    {valuation.comparable_sales.map((sale, i) => (
                      <div key={i} className="flex items-center gap-2 text-sm text-muted-foreground">
                        <ChevronRight className="h-3 w-3" />
                        {sale}
                      </div>
                    ))}
                  </div>
                )}

                {/* Actions */}
                <div className="flex gap-2 pt-2">
                  <Button variant="outline" className="flex-1" onClick={resetState}>
                    Scan Another
                  </Button>
                  <Button className="flex-1" onClick={handleClose}>
                    Done
                  </Button>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </DialogContent>
    </Dialog>
  );
}
